import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAdminPresets } from '../../lib/useAdminPresets';
import { Plus, X, Save, Pencil, Check, Percent, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../../utils/logger';
import { caymanToday } from '../../utils/dateFormat';
import { computeItemTotal, computeDocTotals, formatDiscountBadge } from '../../utils/discountMath';
import { AutoSaveIndicator } from '../../components/admin/AutoSaveIndicator';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pre-populated services organized by category (matching pricing page)
const PRESET_SERVICES_FALLBACK = {
  "Consultation & Documentation": [
    { name: 'Travel Consultation (30 min)', price: 50 },
    { name: 'Travel Consultation (Additional Hour)', price: 80 },
    { name: 'In-Home Health Certificate', price: 0, note: 'Price varies by destination' },
    { name: 'Cayman Islands Import Permit & Compliance Service (First Pet)', price: 350 },
    { name: 'Cayman Islands Import Permit & Compliance Service (Second Pet)', price: 300 },
    { name: 'Cayman Islands Import Permit & Compliance Service (Third Pet)', price: 250 },
    { name: 'Export Permit Services (First Pet)', price: 500 },
    { name: 'Export Permit Services (Second Pet)', price: 450 },
    { name: 'Export Permit Services (Third Pet)', price: 400 },
    { name: 'Export Permit Endorsement Coordination and Document Collection (First Pet)', price: 85 },
    { name: 'Export Permit Endorsement Coordination and Document Collection (Second Pet)', price: 65 },
    { name: 'Export Permit Endorsement Coordination and Document Collection (Third Pet)', price: 45 },
    { name: 'Pet Passport Application', price: 250 },
    { name: 'IATA Crate/Carrier Compliance Guidance Service', price: 50 },
    { name: 'In-Home Pet Sizing & Measurement Service', price: 75 },
    { name: 'Pet Sizing & IATA Crate/Carrier Compliance Bundle', price: 100 },
    { name: 'Cayman Dog Licensing Convenience Service', price: 75 },
    { name: 'Cayman Islands Import Permit + Dog Licence Bundle (First Pet)', price: 390 },
    { name: 'Cayman Islands Import Permit + Dog Licence Bundle (Second Pet)', price: 340 },
    { name: 'Cayman Islands Import Permit + Dog Licence Bundle (Third Pet)', price: 290 },
    { name: 'Document Delivery Service', price: 0, note: 'Bespoke quotation — based on destination district' }
  ],
  "Travel & Logistics": [
    { name: 'Export Cargo Services', price: 250 },
    { name: 'Flight Arrangements and Airline Coordination Service (First Pet)', price: 175 },
    { name: 'Flight Arrangements and Airline Coordination Service (Additional)', price: 85 },
    { name: 'International Broker Coordination Service (First Pet)', price: 175 },
    { name: 'International Broker Coordination Service (Additional)', price: 85 },
    { name: 'Brokerage Travel Services', price: 0, note: 'Bespoke quotation' },
    { name: 'Cargo & Customs Pre-Clearance (First Pet)', price: 300 },
    { name: 'Cargo & Customs Pre-Clearance (Second Pet)', price: 250 },
    { name: 'Cargo & Customs Pre-Clearance (Third Pet)', price: 200 },
    { name: 'Transportation Coordination Service', price: 0, note: 'Bespoke quotation — pets, kennel weight & location' },
    { name: 'Custom Crate Builder Coordination Service', price: 75 }
  ],
  "Health & Compliance": [
    { name: 'CDC Dog Import Application', price: 50 },
    { name: 'Annual Document Maintenance (First Pet)', price: 100 },
    { name: 'Annual Document Maintenance (Additional)', price: 75 },
    { name: 'Rabies Vaccination Scheduling', price: 25 },
    { name: 'Rabies Vaccination Service (First Pet)', price: 100 },
    { name: 'Rabies Vaccination Service (Additional)', price: 75 },
    { name: 'Microchip Implantation (First Pet)', price: 80 },
    { name: 'Microchip Implantation (Additional)', price: 65 }
  ],
  "Other": [
    { name: 'Custom Service', price: 0 }
  ]
};

export const CreateInvoice = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const password = sessionStorage.getItem('admin_password');
  const PRESET_SERVICES = useAdminPresets(PRESET_SERVICES_FALLBACK);
  const isEditMode = !!id;
  
  const [loading, setLoading] = useState(isEditMode);
  
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    client_address: '',
    items: [],
    notes: '',
    due_date: '',
    payment_terms: 'Payment is due in full at the time of invoicing.',
    bank_name: 'Cayman National Bank',
    account_holder: 'Samantha Dorman T/A Pawsome Ventures',
    account_type: 'CI Savings',
    account_number: '012-86563',
    payment_reference: '',
    discount_type: '',       // '' (none) | 'percent' | 'fixed'
    discount_value: 0,
    discount_label: ''
  });
  
  const [currentItem, setCurrentItem] = useState({
    description: '',
    quantity: 1,
    unit_price: 0,
    discount_type: '',
    discount_value: 0,
    discount_label: ''
  });

  // Inline edit state: null = not editing, else { index, description, quantity, unit_price }
  const [editingItem, setEditingItem] = useState(null);
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // ---- Auto-save (draft only, edit mode only) ----
  const AUTOSAVE_DELAY_MS = 15000;
  const [lastAutoSavedAt, setLastAutoSavedAt] = useState(null);
  const autoSaveTimerRef = useRef(null);
  const skipNextAutoSaveRef = useRef(true);
  const savingRef = useRef(false);
  useEffect(() => {
    savingRef.current = saving;
  }, [saving]);

  React.useEffect(() => {
    if (!password) {
      navigate('/admin/login');
      return;
    }
    
    if (isEditMode && id) {
      fetchInvoiceForEdit();
    } else if (!isEditMode) {
      // Prefill from ?client_email=… (e.g., "New Invoice" from a client's page)
      const params = new URLSearchParams(location.search);
      const prefillEmail = params.get('client_email');
      if (prefillEmail) {
        prefillFromClientEmail(prefillEmail);
      }
    }
  }, [id]);

  const prefillFromClientEmail = async (email) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/clients/search?query=${encodeURIComponent(email)}`, {
        headers: { Authorization: `Basic ${btoa(':' + password)}` },
      });
      if (!res.ok) return;
      const list = await res.json();
      const match = list.find(c => (c.email || '').toLowerCase() === email.toLowerCase()) || list[0];
      if (match) selectClient(match);
    } catch (err) {
      logger.error('Prefill from client_email failed:', err);
    }
  };

  const fetchInvoiceForEdit = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/invoices/${id}`, {
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`
        }
      });

      if (response.ok) {
        const invoice = await response.json();
        setFormData({
          client_name: invoice.client_name,
          client_email: invoice.client_email,
          client_phone: invoice.client_phone || '',
          client_address: invoice.client_address || '',
          items: invoice.items,
          notes: invoice.notes || '',
          status: invoice.status,
          invoice_date: (invoice.invoice_date || '').slice(0, 10),
          due_date: (invoice.due_date || '').slice(0, 10),
          payment_terms: invoice.payment_terms || 'Payment is due in full at the time of invoicing.',
          bank_name: invoice.bank_name || 'Cayman National Bank',
          account_holder: invoice.account_holder || 'Samantha Dorman T/A Pawsome Ventures',
          account_type: invoice.account_type || 'CI Savings',
          account_number: invoice.account_number || '012-86563',
          payment_reference: invoice.payment_reference || '',
          discount_type: invoice.discount_type || '',
          discount_value: invoice.discount_value || 0,
          discount_label: invoice.discount_label || ''
        });
      } else {
        alert('Failed to load invoice');
        navigate('/admin/invoices');
      }
    } catch (err) {
      logger.error('Failed to fetch invoice:', err);
      alert('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  // ---- Auto-save effect: debounced PUT while editing a draft invoice ----
  useEffect(() => {
    if (!isEditMode || !id) return;                        // never on /new
    if ((formData.status || '') !== 'draft') return;       // never on non-draft invoices
    if (skipNextAutoSaveRef.current) {
      skipNextAutoSaveRef.current = false;
      return;
    }
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      if (savingRef.current) return;
      saveInvoice({ asDraft: true, silent: true });
    }, AUTOSAVE_DELAY_MS);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const searchClients = async (query) => {
    if (query.length < 2) {
      setClientSuggestions([]);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/clients/search?query=${encodeURIComponent(query)}`, {
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`
        }
      });

      if (response.ok) {
        const clients = await response.json();
        setClientSuggestions(clients);
        setShowSuggestions(true);
      }
    } catch (err) {
      logger.error('Failed to search clients:', err);
    }
  };

  const handleClientSearch = (e) => {
    const value = e.target.value;
    setClientSearch(value);
    setFormData({ ...formData, client_name: value });
    searchClients(value);
  };

  const selectClient = (client) => {
    setFormData({
      ...formData,
      client_name: client.name,
      client_email: client.email,
      client_phone: client.phone || '',
      client_address: client.address || ''
    });
    setClientSearch(client.name);
    setShowSuggestions(false);
    setClientSuggestions([]);
  };

  const handleAddItem = () => {
    if (!currentItem.description || currentItem.unit_price <= 0) {
      alert('Please enter item description and price');
      return;
    }
    
    const total = computeItemTotal(currentItem);
    setFormData({
      ...formData,
      // _key is a stable client-side id for React keys (stripped before API send)
      items: [...formData.items, { ...currentItem, total, _key: `item-${Date.now()}-${Math.random().toString(36).slice(2, 9)}` }]
    });
    
    // Reset current item
    setCurrentItem({
      description: '',
      quantity: 1,
      unit_price: 0,
      discount_type: '',
      discount_value: 0,
      discount_label: ''
    });
  };

  const handleRemoveItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
    if (editingItem && editingItem.index === index) setEditingItem(null);
  };

  const handleStartEdit = (index) => {
    const it = formData.items[index];
    setEditingItem({
      index,
      description: it.description,
      quantity: it.quantity,
      unit_price: it.unit_price,
      discount_type: it.discount_type || '',
      discount_value: it.discount_value || 0,
      discount_label: it.discount_label || ''
    });
  };

  const handleSaveEdit = () => {
    if (!editingItem) return;
    if (!editingItem.description || editingItem.unit_price <= 0) {
      alert('Please enter item description and a price greater than zero.');
      return;
    }
    const total = computeItemTotal(editingItem);
    const updated = [...formData.items];
    updated[editingItem.index] = {
      ...updated[editingItem.index],
      description: editingItem.description,
      quantity: editingItem.quantity,
      unit_price: editingItem.unit_price,
      discount_type: editingItem.discount_type || null,
      discount_value: editingItem.discount_value || 0,
      discount_label: editingItem.discount_label || '',
      total,
    };
    setFormData({ ...formData, items: updated });
    setEditingItem(null);
  };

  const handleCancelEdit = () => setEditingItem(null);

  const handlePresetSelect = (e) => {
    const serviceName = e.target.value;
    if (!serviceName) return;
    
    // Find service in all categories
    let foundService = null;
    for (const category in PRESET_SERVICES) {
      const service = PRESET_SERVICES[category].find(s => s.name === serviceName);
      if (service) {
        foundService = service;
        break;
      }
    }
    
    if (foundService) {
      setCurrentItem({
        ...currentItem,
        description: foundService.name,
        unit_price: foundService.price
      });
    }
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.total || 0), 0);
  };

  const totals = computeDocTotals(formData.items, {
    discount_type: formData.discount_type,
    discount_value: formData.discount_value,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    await saveInvoice({ asDraft: false });
  };

  const saveInvoice = async ({ asDraft, silent = false }) => {
    setError('');
    setSaving(true);

    if (!asDraft && formData.items.length === 0) {
      setError('Please add at least one item');
      setSaving(false);
      return;
    }

    const { subtotal, discountAmount, total } = computeDocTotals(formData.items, {
      discount_type: formData.discount_type,
      discount_value: formData.discount_value,
    });
    // Strip client-side-only _key helper before sending to backend
    const cleanItems = formData.items.map(({ _key, ...rest }) => ({
      ...rest,
      discount_type: rest.discount_type || null,
      discount_value: rest.discount_value || 0,
    }));
    const invoiceData = {
      client_name: formData.client_name,
      client_email: formData.client_email,
      client_phone: formData.client_phone,
      client_address: formData.client_address,
      items: cleanItems,
      subtotal: subtotal,
      tax: 0,
      total: total,
      currency: 'CI$',
      // Force draft status on Save-Draft; never accidentally promote a partial doc.
      status: asDraft ? 'draft' : (formData.status || 'pending'),
      notes: formData.notes,
      due_date: formData.due_date || null,
      invoice_date: formData.invoice_date || caymanToday(),
      payment_terms: formData.payment_terms,
      bank_name: formData.bank_name,
      account_holder: formData.account_holder,
      account_type: formData.account_type,
      account_number: formData.account_number,
      payment_reference: formData.payment_reference,
      discount_type: formData.discount_type || null,
      discount_value: Number(formData.discount_value) || 0,
      discount_amount: discountAmount,
      discount_label: (formData.discount_label || '').trim() || null
    };

    try {
      const url = isEditMode
        ? `${API_URL}/api/admin/invoices/${id}`
        : `${API_URL}/api/admin/invoices`;

      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${btoa(':' + password)}`
        },
        body: JSON.stringify(invoiceData)
      });

      if (response.ok) {
        if (asDraft) {
          if (!silent) toast.success('Draft saved — you can return to keep editing anytime.');
          if (!isEditMode) {
            const result = await response.json();
            // Transition into edit mode so subsequent saves are PUTs on the same doc.
            navigate(`/admin/invoices/${result.id}/edit`, { replace: true });
          }
          setLastAutoSavedAt(new Date());
          setSaving(false);
          return;
        }
        if (isEditMode) {
          navigate(`/admin/invoices/${id}`);
        } else {
          navigate('/admin/invoices');
        }
      } else if (response.status === 401) {
        navigate('/admin/login');
      } else {
        setError(isEditMode ? 'Failed to update invoice' : 'Failed to create invoice');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-foreground/60">Loading invoice...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between admin-header-row">
            <div>
              <h1 className="text-2xl font-bold text-primary">
                {isEditMode ? 'Edit Invoice' : 'Create Invoice'}
              </h1>
              <p className="text-sm text-foreground/60">
                {isEditMode ? 'Update invoice details' : 'Generate a new invoice for your client'}
              </p>
            </div>
            <button
              onClick={() => isEditMode ? navigate(`/admin/invoices/${id}`) : navigate('/admin/invoices')}
              className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 transition-colors"
            >
              ← Back to Invoices
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Client Information */}
              <div className="bg-white rounded-xl p-6 border border-border shadow-sm">
                <h2 className="text-lg font-bold text-foreground mb-4">Client Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 relative">
                    <label className="block text-sm font-medium text-foreground/70 mb-2">
                      Client Name * 
                      <span className="text-xs text-foreground/50 ml-2">(Start typing to search existing clients)</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={clientSearch || formData.client_name}
                      onChange={handleClientSearch}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Start typing client name..."
                    />
                    {showSuggestions && clientSuggestions.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {clientSuggestions.map((client) => (
                          <div
                            key={client.id || client.email}
                            onClick={() => selectClient(client)}
                            className="px-4 py-3 hover:bg-primary/5 cursor-pointer border-b border-border last:border-b-0"
                          >
                            <div className="font-medium text-foreground">{client.name}</div>
                            <div className="text-sm text-foreground/60">{client.email}</div>
                            {client.total_invoices > 0 && (
                              <div className="text-xs text-primary mt-1">
                                {client.total_invoices} invoice{client.total_invoices !== 1 ? 's' : ''} • CI${client.total_spent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} total
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground/70 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.client_email}
                      onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                      className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground/70 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.client_phone}
                      onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                      className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-foreground/70 mb-2">
                      Address
                    </label>
                    <textarea
                      value={formData.client_address}
                      onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                      className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      rows="2"
                    />
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div className="bg-white rounded-xl p-6 border border-border shadow-sm">
                <h2 className="text-lg font-bold text-foreground mb-4">Services / Items</h2>
                
                {/* Add Item Form */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                    <div className="md:col-span-12">
                      <label className="block text-sm font-medium text-foreground/70 mb-2">
                        Quick Select Service
                      </label>
                      <select
                        onChange={handlePresetSelect}
                        value=""
                        className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="">-- Select a service by category --</option>
                        {Object.entries(PRESET_SERVICES).map(([category, services]) => (
                          <optgroup key={category} label={category}>
                            {services.map((service) => (
                              <option key={service.name} value={service.name}>
                                {service.name} {service.price > 0 ? `(CI$${service.price})` : service.note ? `(${service.note})` : ''}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                      <p className="text-xs text-foreground/60 mt-1">
                        Select a preset service or manually enter a custom service below
                      </p>
                    </div>
                    <div className="md:col-span-6">
                      <label className="block text-sm font-medium text-foreground/70 mb-2">
                        Description *
                      </label>
                      <input
                        type="text"
                        value={currentItem.description}
                        onChange={(e) => setCurrentItem({ ...currentItem, description: e.target.value })}
                        className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Service description"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-foreground/70 mb-2">
                        Qty
                      </label>
                      <input
                        type="number"
                        min="0.25"
                        step="0.25"
                        value={currentItem.quantity}
                        onChange={(e) => setCurrentItem({ ...currentItem, quantity: parseFloat(e.target.value) || 1 })}
                        className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className="block text-sm font-medium text-foreground/70 mb-2">
                        Unit Price (CI$)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={currentItem.unit_price}
                        onChange={(e) => setCurrentItem({ ...currentItem, unit_price: parseFloat(e.target.value) || 0 })}
                        className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div className="md:col-span-1 flex items-end">
                      <button
                        type="button"
                        onClick={handleAddItem}
                        className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                      >
                        <Plus className="w-5 h-5 mx-auto" />
                      </button>
                    </div>

                    {/* Optional per-item discount */}
                    <div className="md:col-span-3">
                      <label className="block text-sm font-medium text-foreground/70 mb-2">Item discount</label>
                      <select
                        value={currentItem.discount_type}
                        onChange={(e) => setCurrentItem({ ...currentItem, discount_type: e.target.value })}
                        className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm text-red-700 bg-red-50"
                        data-testid="add-invoice-item-discount-type"
                      >
                        <option value="">No discount</option>
                        <option value="percent">Percentage (%)</option>
                        <option value="fixed">Fixed (CI$)</option>
                      </select>
                    </div>
                    <div className="md:col-span-3">
                      <label className="block text-sm font-medium text-foreground/70 mb-2">Discount value</label>
                      <input
                        type="number" min="0" step="0.01"
                        value={currentItem.discount_value}
                        onChange={(e) => setCurrentItem({ ...currentItem, discount_value: parseFloat(e.target.value) || 0 })}
                        disabled={!currentItem.discount_type}
                        placeholder={currentItem.discount_type === 'percent' ? 'e.g. 10' : currentItem.discount_type === 'fixed' ? 'e.g. 50.00' : ''}
                        className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm disabled:bg-gray-100"
                        data-testid="add-invoice-item-discount-value"
                      />
                    </div>
                    <div className="md:col-span-6">
                      <label className="block text-sm font-medium text-foreground/70 mb-2">Discount label (optional)</label>
                      <input
                        type="text"
                        value={currentItem.discount_label}
                        onChange={(e) => setCurrentItem({ ...currentItem, discount_label: e.target.value })}
                        placeholder="e.g. Multi-pet"
                        disabled={!currentItem.discount_type}
                        className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm disabled:bg-gray-100"
                      />
                    </div>
                  </div>
                </div>

                {/* Items List */}
                {formData.items.length > 0 ? (
                  <div className="space-y-2">
                    {formData.items.map((item, idx) => {
                      const isEditing = editingItem && editingItem.index === idx;
                      if (isEditing) {
                        return (
                          <div key={item._key || `existing-${idx}`} className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2" data-testid={`editing-item-${idx}`}>
                            <input
                              type="text"
                              value={editingItem.description}
                              onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                              className="w-full px-3 py-2 text-sm border border-border rounded-lg"
                              placeholder="Description"
                            />
                            <div className="flex gap-2 flex-wrap">
                              <input
                                type="number"
                                min="1"
                                value={editingItem.quantity}
                                onChange={(e) => setEditingItem({ ...editingItem, quantity: parseFloat(e.target.value) || 0 })}
                                className="w-20 px-3 py-2 text-sm border border-border rounded-lg"
                                placeholder="Qty"
                              />
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={editingItem.unit_price}
                                onChange={(e) => setEditingItem({ ...editingItem, unit_price: parseFloat(e.target.value) || 0 })}
                                className="flex-1 px-3 py-2 text-sm border border-border rounded-lg"
                                placeholder="Unit Price (CI$)"
                              />
                              <select
                                value={editingItem.discount_type || ''}
                                onChange={(e) => setEditingItem({ ...editingItem, discount_type: e.target.value })}
                                className="px-2 py-2 text-xs border border-red-200 rounded-lg text-red-700 bg-red-50"
                                data-testid={`edit-invoice-item-discount-type-${idx}`}
                              >
                                <option value="">No discount</option>
                                <option value="percent">% off</option>
                                <option value="fixed">CI$ off</option>
                              </select>
                              {editingItem.discount_type && (
                                <input
                                  type="number" min="0" step="0.01"
                                  value={editingItem.discount_value || 0}
                                  onChange={(e) => setEditingItem({ ...editingItem, discount_value: parseFloat(e.target.value) || 0 })}
                                  className="w-24 px-2 py-2 text-xs border border-red-200 rounded-lg"
                                  placeholder="Value"
                                  data-testid={`edit-invoice-item-discount-value-${idx}`}
                                />
                              )}
                              <button
                                type="button"
                                onClick={handleSaveEdit}
                                className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-1 text-sm"
                                data-testid={`save-edit-item-${idx}`}
                              >
                                <Check className="w-4 h-4" /> Save
                              </button>
                              <button
                                type="button"
                                onClick={handleCancelEdit}
                                className="px-3 py-2 border border-border text-foreground rounded-lg hover:bg-gray-50 text-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div key={item._key || `existing-${idx}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="flex-1">
                            <div className="font-medium text-foreground">{item.description}</div>
                            <div className="text-sm text-foreground/60">
                              {item.quantity} × CI${item.unit_price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                            {item.discount_type && item.discount_value > 0 && (
                              <div className="text-xs text-red-600 mt-0.5">
                                {formatDiscountBadge(item)}{item.discount_label ? ` — ${item.discount_label}` : ''}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-foreground">CI${item.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            <button
                              type="button"
                              onClick={() => handleStartEdit(idx)}
                              className="p-2 sm:p-1 text-primary hover:bg-primary/10 rounded transition-colors"
                              title="Edit"
                              data-testid={`edit-item-${idx}`}
                            >
                              <Pencil className="w-5 h-5 sm:w-4 sm:h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveItem(idx)}
                              className="p-2 sm:p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Remove"
                            >
                              <X className="w-5 h-5 sm:w-4 sm:h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-foreground/40">
                    No items added yet. Add services above.
                  </div>
                )}

                {formData.items.length > 0 && (
                  <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Percent className="w-4 h-4 text-red-600" />
                      <h3 className="font-semibold text-red-700">Overall discount (optional)</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-foreground/70 mb-1">Type</label>
                        <select
                          value={formData.discount_type}
                          onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                          className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white"
                          data-testid="invoice-discount-type"
                        >
                          <option value="">None</option>
                          <option value="percent">Percentage</option>
                          <option value="fixed">Fixed amount</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground/70 mb-1">
                          Value {formData.discount_type === 'percent' ? '(%)' : formData.discount_type === 'fixed' ? '(CI$)' : ''}
                        </label>
                        <input
                          type="number" min="0" step="0.01"
                          value={formData.discount_value}
                          onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) || 0 })}
                          disabled={!formData.discount_type}
                          className="w-full px-3 py-2 border border-border rounded-lg text-sm disabled:bg-gray-100"
                          data-testid="invoice-discount-value"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-foreground/70 mb-1">Label (optional — shown on PDF)</label>
                        <input
                          type="text"
                          value={formData.discount_label}
                          onChange={(e) => setFormData({ ...formData, discount_label: e.target.value })}
                          placeholder="e.g. Senior citizen, Multi-pet"
                          disabled={!formData.discount_type}
                          className="w-full px-3 py-2 border border-border rounded-lg text-sm disabled:bg-gray-100"
                          data-testid="invoice-discount-label"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Additional Details */}
              <div className="bg-white rounded-xl p-6 border border-border shadow-sm">
                <h2 className="text-lg font-bold text-foreground mb-4">Additional Details</h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground/70 mb-2" htmlFor="invoice-date-input">
                        Invoice Date
                      </label>
                      <input
                        id="invoice-date-input"
                        data-testid="invoice-date-input"
                        type="date"
                        value={formData.invoice_date}
                        onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                        className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <p className="text-xs text-foreground/50 mt-1">
                        Leave blank on new invoices to use today (Cayman time).
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground/70 mb-2">
                        Due Date
                      </label>
                      <input
                        type="date"
                        value={formData.due_date}
                        onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                        className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                  
                  {/* Payment Information */}
                  <div className="bg-white rounded-xl p-6 border border-border shadow-sm">
                    <h2 className="text-lg font-bold text-foreground mb-4">Payment Information</h2>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground/70 mb-2">
                          Payment Terms
                        </label>
                        <input
                          type="text"
                          value={formData.payment_terms}
                          onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                          className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Payment is due in full..."
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground/70 mb-2">
                            Bank Name
                          </label>
                          <input
                            type="text"
                            value={formData.bank_name}
                            onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                            className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground/70 mb-2">
                            Account Holder
                          </label>
                          <input
                            type="text"
                            value={formData.account_holder}
                            onChange={(e) => setFormData({ ...formData, account_holder: e.target.value })}
                            className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground/70 mb-2">
                            Account Type
                          </label>
                          <input
                            type="text"
                            value={formData.account_type}
                            onChange={(e) => setFormData({ ...formData, account_type: e.target.value })}
                            className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground/70 mb-2">
                            Account Number
                          </label>
                          <input
                            type="text"
                            value={formData.account_number}
                            onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                            className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-foreground/70 mb-2">
                          Payment Reference (Optional)
                        </label>
                        <input
                          type="text"
                          value={formData.payment_reference}
                          onChange={(e) => setFormData({ ...formData, payment_reference: e.target.value })}
                          className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Leave blank to use client first name"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-foreground/70 mb-2">
                      Notes / Additional Instructions
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      rows="4"
                      placeholder="Additional notes or instructions..."
                      lang="en-GB"
                      spellCheck="true"
                      autoCorrect="on"
                      autoCapitalize="sentences"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl p-6 border border-border shadow-sm sticky top-6">
                <h2 className="text-lg font-bold text-foreground mb-4">Invoice Summary</h2>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-foreground/70">
                    <span>Subtotal:</span>
                    <span className="font-semibold" data-testid="invoice-summary-subtotal">CI${totals.subtotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  {totals.discountAmount > 0 && (
                    <div className="flex justify-between text-red-700" data-testid="invoice-summary-discount-row">
                      <span>
                        Discount{formData.discount_type === 'percent' ? ` (${formData.discount_value}%)` : ''}{formData.discount_label ? ` — ${formData.discount_label}` : ''}:
                      </span>
                      <span className="font-semibold">−CI${totals.discountAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-foreground/70">
                    <span>Tax:</span>
                    <span className="font-semibold">CI$0.00</span>
                  </div>
                  <div className="border-t border-border pt-3 flex justify-between text-lg font-bold text-foreground">
                    <span>Total:</span>
                    <span className="text-primary" data-testid="invoice-summary-total">CI${totals.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <div className="mb-3 flex justify-center">
                  <AutoSaveIndicator lastAutoSavedAt={lastAutoSavedAt} active={isEditMode && (formData.status || '') === 'draft'} />
                </div>

                <button
                  type="submit"
                  disabled={saving || formData.items.length === 0}
                  className="w-full px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Save className="w-5 h-5" />
                  {saving ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Invoice' : 'Create Invoice')}
                </button>

                <button
                  type="button"
                  onClick={() => saveInvoice({ asDraft: true })}
                  disabled={saving}
                  data-testid="save-invoice-draft-btn"
                  className="w-full mt-3 px-6 py-3 border-2 border-primary text-primary rounded-lg hover:bg-primary/5 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  title="Save what you have so far — no required fields, come back later to keep editing"
                >
                  <FileText className="w-5 h-5" />
                  {saving ? 'Saving…' : 'Save Draft'}
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/admin/invoices')}
                  className="w-full mt-3 px-6 py-3 border border-border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
